import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useAddAddress, useMyAddresses } from "@/features/panel/address/address.api";
import { PlaceSearch } from "@/features/panel/address/PlaceSearch";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ArrowLeft, ArrowRight, ShoppingBag } from "lucide-react";
import {
  MX_STATES,
  couponDiscountCents,
  defaultAddressOf,
  shippingAddressSchema,
  type CouponPublic,
  type PaymentMethod,
  type SavedAddress,
} from "@nh/shared";
import { Alert, Button, Form, Select, TextField, TextareaField } from "@/shared/ui/core";
import { Stepper } from "@/shared/ui/wizard/Stepper";
import { EmptyState } from "@/shared/ui/ui";
import { useAuth } from "@/features/auth/auth.context";
import { CartProvider, useCart } from "@/features/cart";
import {
  clearStoreCheckout,
  readStoreCheckout,
} from "@/features/store-front/store.api";
import {
  clearFranchiseCheckout,
  readFranchiseCheckout,
} from "@/features/franchise/franchise.draft";
import { createFranchiseOrder } from "@/features/franchise/franchise.api";
import { checkoutCart, type CheckoutResult } from "@/features/cart/cart.checkout";
import { resolveCart, subtotalOf } from "@/features/cart/cart.pricing";
import { ShopLayout } from "@/features/shop/components/ShopLayout";
import { useShopCurrency } from "@/features/shop/useShopCurrency";
import { useShopProducts } from "@/features/shop/useShopProducts";
import { localeOf, numberLocaleOf } from "@/features/shop/shop.catalog";
import { usePriceViewer } from "@/features/shop/usePriceViewer";
import { CheckoutSummary } from "./components/CheckoutSummary";
import { PaymentMethodPicker } from "./components/PaymentMethodPicker";
import { CheckoutSuccess } from "./components/CheckoutSuccess";
import { CouponField } from "./components/CouponField";
import { ShippingOptionsPicker } from "./components/ShippingOptionsPicker";
import { useCheckoutShippingQuote } from "./checkoutShipping";

/**
 * ═══════════════════════════════════════════════════════════════════
 * El checkout de la tienda — réplica del `/shop/checkout` de producción.
 *
 * Tres pasos: INFORMACIÓN (quién recibe y a dónde), REVISIÓN (lo que
 * lleva el pedido) y PAGO (tarjeta, transferencia o efectivo — Anahí,
 * 26-ago). A la derecha, el resumen del carrito acompaña los tres.
 *
 * ── Por qué una página y no el cajón ─────────────────────────────────
 * La primera versión cerraba el pedido dentro del panel del carrito, y
 * al vaciarlo la confirmación quedaba TAPADA por "tu carrito está
 * vacío". El cierre de un pedido merece su pantalla, con espacio para
 * el método de pago y la confirmación.
 *
 * ── Las guardas ──────────────────────────────────────────────────────
 * Sin sesión → al login con esta ruta de regreso (el carrito no se
 * pierde: vive en `localStorage`). Sin carrito → invitación al
 * catálogo, salvo que ya haya un pedido confirmado en pantalla — ese
 * estado gana, porque el éxito VACÍA el carrito a propósito.
 * ═══════════════════════════════════════════════════════════════════
 */

const STEP_LABELS = ["Información", "Revisión", "Pago"] as const;

/**
 * Lo que captura el paso 1. El resto del pedido ya vive en el carrito.
 *
 * La dirección va ESTRUCTURADA (integración Trasso, 27-ago): calle,
 * ciudad, estado y CP por separado — lo que una guía de envío exige, y
 * el único momento en que el cliente los escribe. Los mensajes y las
 * reglas (CP de 5 dígitos) son los del contrato compartido.
 */
const checkoutFormSchema = z.object({
  fullName: z
    .string()
    .trim()
    .min(1, "Escribe el nombre de quien recibe")
    .max(120, "Ese nombre es demasiado largo"),
  phone: z
    .string()
    .trim()
    .min(7, "Escribe un teléfono para contactarte sobre el envío")
    .max(30, "Ese teléfono es demasiado largo"),
  ...shippingAddressSchema.shape,
});

type CheckoutFormValues = z.infer<typeof checkoutFormSchema>;

/**
 * Cada pantalla pública monta su propio `CartProvider` (el estado real
 * vive en `localStorage`, así que todas ven el mismo carrito). El
 * checkout no es la excepción: sin esto, `useCart` revienta al montar.
 */
export function CheckoutPage() {
  return (
    <CartProvider>
      <CheckoutScreen />
    </CartProvider>
  );
}

function CheckoutScreen() {
  const { t, i18n } = useTranslation();
  const locale = localeOf(i18n.language);
  const [shopCurrency, setCurrency] = useShopCurrency();
  const { user, isAuthenticated } = useAuth();
  const { lines, clear } = useCart();

  const [step, setStep] = useState(0);
  const [method, setMethod] = useState<PaymentMethod>(() =>
    readStoreCheckout() ? "transfer" : "card",
  );
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CheckoutResult | null>(null);
  /** El cupón validado por el servidor, o nada. Ver `CouponField`. */
  const [coupon, setCoupon] = useState<CouponPublic | null>(null);

  /*
   * ── El checkout tiene DOS entradas ─────────────────────────────────
   * El carrito de la casa, o la compra directa de una TIENDA
   * personalizada (`readStoreCheckout`). Cuando es de tienda, los
   * renglones vienen del borrador CON su precio ya resuelto por el
   * servidor (el propio del franquiciatario o el de socio), y el cobro
   * viaja con `storeSlug` para que el servidor aplique la MISMA regla
   * y atribuya la venta.
   */
  const [storeDraft] = useState(() => readStoreCheckout());

  /*
   * ── Y una TERCERA: la franquicia y su paquete (Anahí, 29-ago) ──────
   * `/franquicias/adquirir` deja aquí qué se compra —la franquicia con
   * su primer stock, o solo el paquete— y esta pantalla se encarga del
   * resto: dirección, teléfono y método de pago. Antes cada botón de
   * aquella pantalla cerraba la venta por su cuenta, con menos datos que
   * una compra de la tienda.
   *
   * Sus renglones NO son productos del catálogo: son la franquicia y un
   * paquete, con su precio ya resuelto. Por eso el resumen los pinta del
   * borrador y el cobro va por su propio endpoint.
   */
  const [franchiseDraft] = useState(() => readFranchiseCheckout());

  /*
   * En modo TIENDA la moneda se FIJA en pesos: la tienda personalizada
   * vende en MXN (regla del contrato, y el servidor la exige). Dejar el
   * selector activo aquí armaría un pedido que el cobro va a rechazar.
   */
  const currency = storeDraft
    ? ("MXN" as const)
    : (franchiseDraft?.currency ?? shopCurrency);

  /* En la tienda de un franquiciatario NO hay Stripe (Anahí, 27-ago):
   * transferencia a SU cuenta o efectivo con él. El servidor rechaza
   * "card" con storeSlug; aquí ni se ofrece. */
  const metodosDisponibles = storeDraft ? (["transfer", "cash"] as const) : undefined;

  const products = useShopProducts();
  const viewer = usePriceViewer();
  const entries = useMemo(() => {
    const catalog = products.data?.items ?? [];
    if (!storeDraft) return resolveCart(lines, catalog, currency, viewer);
    return storeDraft.items.map((line) => {
      const product = catalog.find((item) => item.id === line.productId);
      const cents = line.unitPriceCents > 0 ? line.unitPriceCents : undefined;
      return {
        line: { productId: line.productId, slug: product?.slug ?? line.productId, quantity: line.quantity },
        product,
        price:
          product && typeof cents === "number"
            ? { cents, currency, isFallbackCurrency: false, tier: "store" as const }
            : null,
      };
    });
  }, [storeDraft, lines, products.data, currency, viewer]);
  const subtotalCarrito = useMemo(() => subtotalOf(entries, currency), [entries, currency]);

  /*
   * La franquicia y el paquete no están en el catálogo de productos, así
   * que su total sale del borrador. El servidor lo vuelve a calcular al
   * cobrar —de la configuración y del catálogo de paquetes—, como en
   * todo lo demás: esto es lo que se ENSEÑA, no lo que se cobra.
   */
  const subtotal = franchiseDraft
    ? {
        cents: franchiseDraft.franchisePriceCents + franchiseDraft.packagePriceCents,
        currency,
        // La franquicia (si va) y el paquete. Ninguno se queda fuera:
        // sus precios vienen resueltos en el borrador.
        countedItems: franchiseDraft.withFranchise ? 2 : 1,
        skippedItems: 0,
      }
    : subtotalCarrito;

  /*
   * Un cupón FIJO vive en una moneda. Si el cliente cambia de moneda con
   * el cupón puesto, se quita solo — dejarlo pintaría un descuento que el
   * servidor va a rechazar al cobrar.
   */
  useEffect(() => {
    setCoupon((current) =>
      current && current.kind === "fixed" && current.currency !== currency ? null : current,
    );
  }, [currency]);

  /** El estimado que ve el cliente: la MISMA aritmética que el servidor. */
  const appliedDiscount = coupon
    ? { code: coupon.code, discountCents: couponDiscountCents(coupon, subtotal.cents) }
    : null;

  /*
   * Los renglones del pedido de franquicia, para el resumen. La
   * franquicia se enseña aunque vaya a $0 —el paquete la incluye—:
   * se está adquiriendo, y un pedido que no la menciona parece que no
   * la trae.
   */
  const franchiseLines = franchiseDraft
    ? [
        ...(franchiseDraft.withFranchise
          ? [
              {
                id: "franquicia",
                name: t("Franquicia Digital (1 año)"),
                priceCents: franchiseDraft.franchisePriceCents,
              },
            ]
          : []),
        {
          id: franchiseDraft.packageId,
          name: franchiseDraft.packageName,
          priceCents: franchiseDraft.packagePriceCents,
        },
      ]
    : undefined;

  const hasItems = franchiseDraft
    ? true
    : storeDraft
      ? storeDraft.items.length > 0
      : lines.length > 0;

  const form = useForm<CheckoutFormValues>({
    resolver: zodResolver(checkoutFormSchema),
    defaultValues: {
      fullName: user ? user.fullName || `${user.firstName} ${user.lastName}`.trim() : "",
      phone: user?.phone ?? "",
      street: "",
      city: "",
      state: "",
      zip: "",
      reference: "",
    },
    mode: "onSubmit",
  });

  /*
   * ═══════════════════════════════════════════════════════════════════
   * LA DIRECCIÓN SE ELIGE, NO SE RE-ESCRIBE (Anahí, 29-ago).
   *
   * "Aquí solo deben aparecer las direcciones que agreguen como oficial
   * desde /socio/perfil, solo debe ser un select [...] pero con la
   * posibilidad de desplegar si tiene más direcciones, y si quiere
   * agregar una nueva dirección, que se agregue y en automático también
   * se agregará en /socio/perfil."
   *
   * Antes el checkout autollenaba los campos con la oficial y los dejaba
   * todos a la vista. Funcionaba, pero pedía revisar cuatro campos en
   * cada compra y no daba forma de elegir OTRA de las guardadas: había
   * que sobrescribirlos a mano, con lo que la lista del perfil dejaba de
   * significar nada.
   *
   * Ahora: un selector con las guardadas —la oficial primero y
   * preseleccionada— y una última opción para escribir una nueva. Los
   * campos solo aparecen en ese caso, que es el único en que hay algo
   * que capturar.
   *
   * La lista viene de `/socio/perfil` y lo nuevo vuelve allá solo: al
   * hacer el pedido se guarda (más abajo), así que no hay dos lugares
   * donde vivan las direcciones de alguien.
   * ═══════════════════════════════════════════════════════════════════
   */
  const savedAddresses = useMyAddresses();
  const saveAddress = useAddAddress();

  const NUEVA = "nueva";
  const direcciones = savedAddresses.data ?? [];
  const [elegida, setElegida] = useState<string | null>(null);

  /* La oficial queda elegida en cuanto llega la lista. `null` mientras
   * tanto: elegir antes de saber qué hay dejaría "nueva" seleccionada
   * medio segundo y el formulario abierto de más. */
  useEffect(() => {
    if (elegida !== null || direcciones.length === 0) return;
    const oficial = defaultAddressOf(direcciones);
    if (oficial) aplicar(oficial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [direcciones, elegida]);

  /** Vuelca una dirección guardada en el formulario y la deja elegida. */
  function aplicar(address: SavedAddress) {
    setElegida(address.id);
    form.reset({
      ...form.getValues(),
      street: address.street,
      city: address.city,
      state: address.state,
      zip: address.zip,
      reference: address.reference ?? "",
    });
  }

  function cambiarDireccion(valor: string) {
    if (valor === NUEVA) {
      setElegida(NUEVA);
      // Los campos se vacían: heredar los de la anterior invita a
      // guardar una copia con una letra cambiada.
      form.reset({ ...form.getValues(), street: "", city: "", state: "", zip: "", reference: "" });
      return;
    }
    const address = direcciones.find((item) => item.id === valor);
    if (address) aplicar(address);
  }

  /* Se capturan campos cuando se pidió una nueva, o cuando no hay
   * ninguna guardada todavía (la primera compra de una cuenta). */
  const capturando = elegida === NUEVA || direcciones.length === 0;

  /** Cómo se lee una dirección en el desplegable. */
  function resumir(address: SavedAddress): string {
    const base = `${address.street}, ${address.city}, ${address.zip}`;
    return address.label ? `${address.label} · ${base}` : base;
  }

  /*
   * ═══════════════════════════════════════════════════════════════════
   * EL ENVÍO LO PAGA QUIEN COMPRA (Anahí, 13-oct-2026).
   *
   * Con la dirección ya capturada (paso de revisión en adelante) se
   * cotiza con Trasso: el servidor elige la caja, guarda la foto del
   * precio y dice si este pedido cobra envío. Aquí se pinta y se elige;
   * al cobrar solo viaja qué cotización y qué servicio.
   *
   * La tienda de un franquiciatario no cotiza: entrega quien vende.
   * ═══════════════════════════════════════════════════════════════════
   */
  const quoteInput = useMemo(() => {
    if (step < 1 || storeDraft) return null;
    const values = form.getValues();
    const items = franchiseDraft
      ? [{ productId: franchiseDraft.packageId, quantity: 1 }]
      : lines.map((line) => ({ productId: line.productId, quantity: line.quantity }));
    if (items.length === 0) return null;
    return {
      currency,
      items,
      fullName: values.fullName,
      phone: values.phone,
      shippingAddress: {
        street: values.street,
        city: values.city,
        state: values.state,
        zip: values.zip,
        ...(values.reference?.trim() ? { reference: values.reference.trim() } : {}),
      },
    };
    // `form` es estable; los valores se leen al pasar de paso a propósito.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, storeDraft, franchiseDraft, lines, currency]);
  const shippingQuote = useCheckoutShippingQuote(quoteInput);
  const quote = shippingQuote.data;
  const [serviceCode, setServiceCode] = useState<string | null>(null);

  /* Lo elegido, si sigue en la cotización vigente; si no (cotización
   * nueva), la recomendación de Trasso — la primera. */
  const chosen = quote?.required
    ? (quote.options.find((option) => option.serviceCode === serviceCode) ?? quote.options[0])
    : undefined;
  const shippingSummary = storeDraft
    ? null
    : quoteInput === null
      ? ({ status: "address" } as const)
      : shippingQuote.isPending
        ? ({ status: "pending" } as const)
        : chosen
          ? ({ status: "charged", cents: chosen.priceCents, label: chosen.carrier } as const)
          : null;
  /* Sin servicio elegido no se avanza cuando el pedido SÍ cobra envío. */
  const shippingBlocked =
    quoteInput !== null && (shippingQuote.isPending || shippingQuote.isError || (quote?.required === true && !chosen));

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: "/checkout" }} />;
  }

  async function realizarPedido() {
    const values = form.getValues();
    setError(null);
    setSending(true);
    try {
      const sourceLines = storeDraft
        ? storeDraft.items.map((line) => ({
            productId: line.productId,
            slug: line.productId,
            quantity: line.quantity,
          }))
        : lines;
      /*
       * La dirección del pedido se queda GUARDADA para la próxima compra
       * (Anahí, 28-ago). Va sin `await` y sin romper nada si falla: es
       * una comodidad, no parte del pedido — un fallo aquí no puede
       * impedir comprar.
       */
      /*
       * Solo si es NUEVA: repetir la misma dirección en cada compra
       * llenaría la lista de copias.
       */
      /*
       * La dirección nueva se guarda sola, y así aparece en
       * /socio/perfil sin que nadie la vuelva a escribir allá (Anahí,
       * 29-ago). Solo cuando se capturó una: eligiendo del desplegable
       * no hay nada que guardar.
       *
       * La comparación por calle y CP sigue como segunda red: dos
       * pedidos seguidos a la misma dirección no pueden llenar la lista
       * de copias.
       */
      const yaGuardada = (savedAddresses.data ?? []).some(
        (address) =>
          address.street.trim().toLowerCase() === values.street.trim().toLowerCase() &&
          address.zip === values.zip,
      );
      if (capturando && !yaGuardada) {
        saveAddress.mutate({
          street: values.street,
          city: values.city,
          state: values.state,
          zip: values.zip,
          ...(values.reference ? { reference: values.reference } : {}),
        });
      }

      /*
       * La franquicia y su paquete van por SU endpoint: sus renglones
       * los arma el servidor (precio de la configuración y del catálogo
       * de paquetes) y de aquí solo viaja qué paquete, si lleva
       * franquicia, a dónde se envía y cómo se paga.
       */
      if (franchiseDraft) {
        const pedido = await createFranchiseOrder({
          currency,
          packageId: franchiseDraft.packageId,
          withFranchise: franchiseDraft.withFranchise,
          phone: values.phone,
          paymentMethod: method,
          shippingAddress: {
            street: values.street,
            city: values.city,
            state: values.state,
            zip: values.zip,
            ...(values.reference?.trim() ? { reference: values.reference.trim() } : {}),
          },
          ...(quote?.required && chosen
            ? { shippingSelection: { quoteId: quote.quoteId, serviceCode: chosen.serviceCode } }
            : {}),
        });
        setResult(pedido);
        clearFranchiseCheckout();
        if (pedido.payment.status === "ready") {
          window.location.assign(pedido.payment.url);
        }
        return;
      }

      const checkout = await checkoutCart(sourceLines, {
        currency,
        fullName: values.fullName,
        phone: values.phone,
        shippingAddress: {
          street: values.street,
          city: values.city,
          state: values.state,
          zip: values.zip,
          ...(values.reference?.trim() ? { reference: values.reference.trim() } : {}),
        },
        paymentMethod: method,
        ...(storeDraft ? { storeSlug: storeDraft.storeSlug } : {}),
        ...(coupon ? { couponCode: coupon.code } : {}),
        ...(quote?.required && chosen
          ? { shippingSelection: { quoteId: quote.quoteId, serviceCode: chosen.serviceCode } }
          : {}),
      });
      setResult(checkout);
      // Lo comprado se limpia DESPUÉS de que el pedido existe: si el
      // servidor lo rechaza, lo que había sigue aquí para corregirlo.
      if (storeDraft) clearStoreCheckout();
      else clear();

      /*
       * ── Con TARJETA se va derecho a Stripe (Anahí, 29-ago) ─────────
       * "Si el método de pago es tarjeta, cuando le den en realizar
       * pedido lo redirige al link de Stripe."
       *
       * Antes se pintaba la confirmación con un botón "Pagar ahora" que
       * abría Stripe en otra pestaña: quien elige tarjeta quiere pagar
       * ahora, y ese paso de más es donde se abandona el carrito. El
       * pedido YA está creado y su folio queda en el correo y en
       * /socio/pedidos, así que no se pierde nada por saltarse la
       * pantalla: al volver de Stripe se llega a /compra/confirmacion.
       *
       * Transferencia y efectivo NO redirigen —no hay a dónde—: esos ven
       * la confirmación con sus instrucciones.
       */
      if (checkout.payment.status === "ready") {
        window.location.assign(checkout.payment.url);
        return;
      }
    } catch (cause) {
      setError(
        cause instanceof Error && cause.message
          ? cause.message
          : t("No se pudo cerrar el pedido. Inténtalo otra vez."),
      );
    } finally {
      setSending(false);
    }
  }

  /*
   * `<Form>` ya envuelve con `handleSubmit`: aquí solo se decide qué
   * pasa cuando el paso 1 valida — avanzar.
   */
  const irARevision = () => setStep(1);

  return (
    <ShopLayout currency={currency} onCurrencyChange={setCurrency}>
      <div className="mx-auto w-full max-w-[1200px] px-4 py-8 sm:px-8">
        {result ? (
          <CheckoutSuccess result={result} />
        ) : !hasItems ? (
          <div className="py-16">
            <EmptyState
              icon={<ShoppingBag className="h-5 w-5 text-ds-ink-3" />}
              title={t("Tu carrito está vacío")}
              description={t("Añade lo que quieras llevarte y vuelve aquí para cerrar tu pedido.")}
              action={
                <Button as={Link} to="/shop" size="sm">
                  {t("Ver el catálogo")}
                </Button>
              }
            />
          </div>
        ) : (
          <>
            <Stepper steps={STEP_LABELS} current={step} label={t("Pasos del pedido")} />

            <div className="mt-8 grid gap-6 lg:grid-cols-[1fr_380px]">
              {/* ── El paso activo ──────────────────────────────────── */}
              <section className="rounded-3xl border border-ds-border bg-white p-6 shadow-ds-float sm:p-8">
                {step === 0 && (
                  <Form form={form} onSubmit={irARevision} className="grid gap-5">
                    <header>
                      <h1 className="text-[22px] font-semibold text-ds-ink">
                        {t("Datos de contacto")}
                      </h1>
                      <p className="mt-1 text-[14px] text-ds-ink-3">
                        {t("Usaremos esta información para confirmar tu pedido.")}
                      </p>
                    </header>

                    <TextField
                      name="fullName"
                      label={t("Nombre completo")}
                      hint={t("Tu nombre tal como aparece en tu identificación.")}
                      autoComplete="name"
                    />
                    <div className="grid gap-5 sm:grid-cols-2">
                      {/*
                        El correo se ENSEÑA y no se edita: la confirmación
                        va a la cuenta con la que se está comprando.
                      */}
                      <div className="grid gap-1">
                        <p className="text-[12px] font-medium text-ds-ink-3">
                          {t("Correo electrónico")}
                        </p>
                        <p className="flex h-11 items-center rounded-xl border border-ds-border bg-ds-cream-2/40 px-3 text-sm text-ds-ink">
                          {user?.email}
                        </p>
                        <p className="text-[11px] text-ds-ink-3">
                          {t("Aquí recibirás la confirmación del pedido.")}
                        </p>
                      </div>
                      <TextField
                        name="phone"
                        label={t("Teléfono")}
                        placeholder="+52 55 1234 5678"
                        hint={t("Para contactarte sobre el envío.")}
                        autoComplete="tel"
                        inputMode="tel"
                      />
                    </div>

                    {/*
                      ── La dirección se ELIGE (Anahí, 29-ago) ──────────
                      El desplegable trae las guardadas en /socio/perfil,
                      con la oficial arriba y ya elegida. Los campos solo
                      salen para una dirección nueva: ver el bloque de
                      `elegida` arriba.
                    */}
                    {direcciones.length > 0 && (
                      <Select
                        label={t("Dirección de entrega")}
                        value={elegida ?? ""}
                        onChange={(event) => cambiarDireccion(event.target.value)}
                        hint={t("Se guardan en tu perfil: elige una o agrega otra.")}
                        options={[
                          ...direcciones.map((address) => ({
                            value: address.id,
                            label: address.isDefault
                              ? `${resumir(address)} — ${t("oficial")}`
                              : resumir(address),
                          })),
                          { value: NUEVA, label: t("+ Agregar una dirección nueva") },
                        ]}
                      />
                    )}

                    {capturando && (
                      <>
                        {/*
                          La dirección, campo por campo: son los datos que
                          la guía de envío (Trasso) exige por separado.
                          Buscar con Google los rellena de un tirón; siguen
                          editables porque Google no sabe el interior ni la
                          referencia. Sin clave, el buscador no se pinta.
                        */}
                        <PlaceSearch
                          onPick={(picked) => {
                            if (picked.street) form.setValue("street", picked.street);
                            if (picked.city) form.setValue("city", picked.city);
                            if (picked.state) form.setValue("state", picked.state);
                            if (picked.zip) form.setValue("zip", picked.zip);
                          }}
                        />

                        <TextField
                          name="street"
                          label={t("Calle, número y colonia")}
                          placeholder={t("Av. Siempre Viva 123, Col. Centro")}
                          autoComplete="street-address"
                        />
                        <div className="grid gap-5 sm:grid-cols-3">
                          <TextField
                            name="city"
                            label={t("Ciudad")}
                            autoComplete="address-level2"
                          />
                          <TextField
                            name="state"
                            label={t("Estado")}
                            autoComplete="address-level1"
                            list="checkout-estados"
                          />
                          <TextField
                            name="zip"
                            label={t("Código postal")}
                            placeholder="06600"
                            inputMode="numeric"
                            maxLength={5}
                            autoComplete="postal-code"
                          />
                        </div>
                        <datalist id="checkout-estados">
                          {MX_STATES.map((estado) => (
                            <option key={estado} value={estado} />
                          ))}
                        </datalist>
                        <TextareaField
                          name="reference"
                          label={t("Referencias (opcional)")}
                          rows={2}
                          placeholder={t("Portón azul, entre calles, indicaciones para quien entrega")}
                        />
                      </>
                    )}

                    <Button type="submit" size="lg" rightIcon={<ArrowRight />}>
                      {t("Continuar a revisión")}
                    </Button>
                  </Form>
                )}

                {step === 1 && (
                  <div className="grid gap-5">
                    <header>
                      <h1 className="text-[22px] font-semibold text-ds-ink">
                        {t("Revisa tu pedido")}
                      </h1>
                      <p className="mt-1 text-[14px] text-ds-ink-3">
                        {t("Confirma que todo esté como lo quieres antes de pagar.")}
                      </p>
                    </header>

                    <dl className="grid gap-3 rounded-2xl border border-ds-border bg-ds-cream-2/40 p-4 text-[14px]">
                      <div>
                        <dt className="font-medium text-ds-ink-3">{t("Recibe")}</dt>
                        <dd className="text-ds-ink">
                          {form.getValues("fullName")} · {form.getValues("phone")}
                        </dd>
                      </div>
                      <div>
                        <dt className="font-medium text-ds-ink-3">{t("Entrega en")}</dt>
                        <dd className="whitespace-pre-line text-ds-ink">
                          {form.getValues("street")}
                          {"\n"}
                          {form.getValues("city")}, {form.getValues("state")}, CP{" "}
                          {form.getValues("zip")}
                          {form.getValues("reference")?.trim()
                            ? `\nRef: ${form.getValues("reference")?.trim()}`
                            : ""}
                        </dd>
                      </div>
                    </dl>

                    {/* ── El envío (13-oct-2026) ─────────────────────────── */}
                    {quoteInput !== null &&
                      (shippingQuote.isPending ? (
                        <p className="text-[14px] text-ds-ink-3">{t("Calculando el envío…")}</p>
                      ) : shippingQuote.isError ? (
                        <Alert variant="error">
                          {shippingQuote.error.message ||
                            t("No pudimos calcular el envío. Revisa la dirección e inténtalo otra vez.")}{" "}
                          <button
                            type="button"
                            className="font-semibold underline"
                            onClick={() => void shippingQuote.refetch()}
                          >
                            {t("Reintentar")}
                          </button>
                        </Alert>
                      ) : quote?.required ? (
                        <ShippingOptionsPicker
                          options={quote.options}
                          value={chosen?.serviceCode ?? null}
                          onChange={setServiceCode}
                          numberLocale={numberLocaleOf(locale)}
                        />
                      ) : (
                        <p className="text-[14px] text-ds-ink-3">
                          {t("El costo y el tiempo de envío se coordinan al confirmar tu pago.")}
                        </p>
                      ))}

                    <div className="flex flex-wrap gap-3">
                      <Button
                        variant="secondary"
                        size="lg"
                        leftIcon={<ArrowLeft />}
                        onClick={() => setStep(0)}
                      >
                        {t("Corregir datos")}
                      </Button>
                      <Button
                        size="lg"
                        rightIcon={<ArrowRight />}
                        disabled={shippingBlocked}
                        onClick={() => setStep(2)}
                      >
                        {t("Continuar al pago")}
                      </Button>
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div className="grid gap-5">
                    <header>
                      <h1 className="text-[22px] font-semibold text-ds-ink">
                        {t("¿Cómo quieres pagar?")}
                      </h1>
                      <p className="mt-1 text-[14px] text-ds-ink-3">
                        {t(
                          "Con tarjeta pagas en línea al confirmar. Con transferencia o efectivo, validamos tu pago y tu pedido cambia de estado.",
                        )}
                      </p>
                    </header>

                    <PaymentMethodPicker value={method} onChange={setMethod} methods={metodosDisponibles} />

                    {/* El cupón, AL FINAL del pedido (Anahí, 27-ago). */}
                    <CouponField
                      currency={currency}
                      applied={coupon}
                      onApply={setCoupon}
                      onRemove={() => setCoupon(null)}
                    />

                    {error && (
                      <Alert variant="error" onDismiss={() => setError(null)}>
                        {error}
                      </Alert>
                    )}

                    <div className="flex flex-wrap gap-3">
                      <Button
                        variant="secondary"
                        size="lg"
                        leftIcon={<ArrowLeft />}
                        onClick={() => setStep(1)}
                      >
                        {t("Volver a revisión")}
                      </Button>
                      <Button
                        size="lg"
                        loading={sending}
                        disabled={shippingBlocked}
                        onClick={() => void realizarPedido()}
                      >
                        {t("Realizar pedido")}
                      </Button>
                    </div>
                  </div>
                )}
              </section>

              {/* ── El resumen, siempre a la vista ──────────────────── */}
              <CheckoutSummary
                entries={entries}
                subtotal={subtotal}
                locale={locale}
                loading={products.isPending}
                coupon={appliedDiscount}
                customLines={franchiseLines}
                shipping={shippingSummary}
              />
            </div>
          </>
        )}
      </div>
    </ShopLayout>
  );
}
